'use client'

import { throttle } from 'lodash'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import { useBreakpoints } from '@src/utils/breakpoints.util'
import { CUSTOM_EVENTS } from '@src/utils/constants.util'
import { cn } from '@src/utils/tailwind'

import { Container } from '../Container/Container'

const SCROLL_THRESHOLD_DEFAULT = 20

export interface StickyFooterProps {
  previewMode?: boolean
  scrollThresholdDesktop?: number
  scrollThresholdMobile?: number
  attributes?: any
  children?: ReactNode
  useTargetElement?: boolean
  targetElement?: string
  className?: string
}

export const StickyFooter = ({
  previewMode,
  scrollThresholdDesktop,
  scrollThresholdMobile,
  attributes,
  children,
  useTargetElement = false,
  targetElement = '',
  className,
}: StickyFooterProps) => {
  const [isVisible, setVisible] = useState(false)
  const prevVisibleRef = useRef(isVisible)
  const { isMobile } = useBreakpoints()

  const scrollThreshold = useMemo(() => {
    return (isMobile ? scrollThresholdMobile : scrollThresholdDesktop) ?? SCROLL_THRESHOLD_DEFAULT
  }, [scrollThresholdDesktop, scrollThresholdMobile, isMobile])

  useEffect(() => {
    const onScroll = throttle(() => {
      if (useTargetElement) {
        const el = document.querySelector(targetElement)
        if (el) {
          const rect = el.getBoundingClientRect()
          const isOutOfTop = rect.bottom < 0
          setVisible(isOutOfTop)
        }
      } else {
        setVisible(window.scrollY > scrollThreshold)
      }
    }, 300)

    window.addEventListener('scroll', onScroll)

    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [scrollThreshold, targetElement, useTargetElement])

  useEffect(() => {
    if (prevVisibleRef.current !== isVisible) {
      const eventName = isVisible
        ? CUSTOM_EVENTS.stickyFooterVisible
        : CUSTOM_EVENTS.stickyFooterHidden
      window.dispatchEvent(new CustomEvent(eventName))
      prevVisibleRef.current = isVisible
    }
  }, [isVisible])

  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 z-10 flex min-h-[100px] w-full translate-y-full items-center bg-bodi-black-text py-5 text-white transition-all lg:py-8',
        attributes?.className,
        className,
        {
          'translate-y-0': isVisible || previewMode,
        }
      )}
    >
      <Container>{children}</Container>
    </div>
  )
}
